<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center space-x-3">
            <UButton to="/broadcasts" color="gray" variant="ghost" icon="i-ph-arrow-left" />
            <h1 class="text-2xl font-semibold text-gray-700">Broadcast Report</h1>
        </div>

        <div v-if="pending" class="flex items-center justify-center py-20 text-gray-400">
            <UIcon name="i-ph-circle-notch-bold" class="animate-spin text-2xl" />
        </div>

        <template v-else-if="report">
            <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm p-5 space-y-3">
                <div class="flex items-start justify-between gap-4">
                    <div class="space-y-1">
                        <p class="text-xs text-gray-400 font-mono">
                            {{ report.broadcast.number.toString().padStart(8, '0') }}
                        </p>
                        <h2 class="text-lg font-semibold text-gray-800">{{ report.broadcast.title }}</h2>
                        <p v-if="report.broadcast.sent_at" class="text-sm text-gray-500">
                            Sent {{ formatDate(report.broadcast.sent_at) }}
                        </p>
                        <p v-else-if="report.broadcast.scheduled_at" class="text-sm text-gray-500">
                            Scheduled {{ formatDate(report.broadcast.scheduled_at) }}
                        </p>
                    </div>
                    <div class="flex items-center gap-2 flex-wrap justify-end">
                        <UBadge
                            :label="channelLabel(report.broadcast.template?.channel)"
                            :color="channelColor(report.broadcast.template?.channel)"
                            size="sm"
                            variant="subtle"
                        />
                        <UBadge
                            :label="report.broadcast.status"
                            :color="statusColor(report.broadcast.status)"
                            size="sm"
                            variant="subtle"
                        />
                    </div>
                </div>

                <div class="flex items-center gap-6 text-sm text-gray-600 border-t pt-3">
                    <div>
                        <span class="text-gray-400">Estimated recipients</span>
                        <span class="ml-2 font-medium">{{ report.broadcast.estimated_recipients ?? '–' }}</span>
                    </div>
                    <div>
                        <span class="text-gray-400">Estimated cost</span>
                        <span class="ml-2 font-medium">
                            {{ report.broadcast.estimated_cost != null ? `$${report.broadcast.estimated_cost.toFixed(4)}` : '–' }}
                        </span>
                    </div>
                    <div>
                        <span class="text-gray-400">Actual sent</span>
                        <span class="ml-2 font-medium">{{ report.totals.sent + report.totals.delivered }}</span>
                    </div>
                    <div>
                        <span class="text-gray-400">Actual cost</span>
                        <span class="ml-2 font-medium">
                            {{ actualCost != null ? `$${actualCost.toFixed(4)}` : '–' }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="flex flex-wrap gap-2">
                <UBadge
                    v-for="(count, status) in report.totals"
                    :key="status"
                    :label="`${status}: ${count}`"
                    :color="msgStatusColor(status)"
                    size="sm"
                    variant="subtle"
                />
            </div>

            <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                <UTable
                    :rows="report.recipients"
                    :columns="columns"
                    :loading="pending"
                    :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                    :empty-state="{ icon: 'i-ph-envelope-duotone', label: 'No messages.' }"
                    :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                    class="h-full"
                >
                    <template #customer_id-data="{ row }">
                        <span class="font-mono text-xs text-gray-500">{{ row.customer_id ?? '–' }}</span>
                    </template>
                    <template #destination-data="{ row }">
                        <span class="text-sm">{{ row.destination ?? '–' }}</span>
                    </template>
                    <template #status-data="{ row }">
                        <UBadge
                            :label="row.status"
                            :color="msgStatusColor(row.status)"
                            size="sm"
                            variant="subtle"
                        />
                    </template>
                    <template #error-data="{ row }">
                        <span v-if="row.error" class="text-xs text-red-500 max-w-xs block truncate" :title="row.error">
                            {{ row.error }}
                        </span>
                        <span v-else class="text-gray-300">–</span>
                    </template>
                    <template #created_at-data="{ row }">
                        <span class="text-sm text-gray-500 whitespace-nowrap">{{ formatDate(row.created_at) }}</span>
                    </template>
                </UTable>
            </div>

            <div class="shrink-0 flex items-center justify-between text-sm text-gray-500">
                <span>Page {{ page + 1 }}</span>
                <div class="flex gap-2">
                    <UButton :disabled="page === 0" color="gray" variant="ghost" icon="i-ph-caret-left" @click="page--" />
                    <UButton :disabled="report.recipients.length < limit" color="gray" variant="ghost" icon="i-ph-caret-right" @click="page++" />
                </div>
            </div>
        </template>

        <div v-else class="text-center py-20 text-gray-400">Broadcast not found.</div>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Broadcast Report' })

const route = useRoute()
const page = ref(0)
const limit = 50

const { data: report, pending, refresh } = await useAsyncData<{
    broadcast: Record<string, any>
    totals: Record<string, number>
    recipients: any[]
}>(
    `broadcast-report-${route.params.id}`,
    () => $fetch(`/api/broadcasts/${route.params.id}/report`, {
        query: { page: page.value, limit },
    }),
    { watch: [page] },
)

const actualCost = computed(() => {
    const totals = (report.value as any)?.broadcast?.totals
    if (totals && typeof totals === 'object' && 'actual_cost' in totals) return totals.actual_cost as number
    return null
})

const columns = [
    { key: 'customer_id', label: 'Customer' },
    { key: 'destination', label: 'Destination' },
    { key: 'status', label: 'Status' },
    { key: 'error', label: 'Error', class: 'w-full' },
    { key: 'created_at', label: 'Time' },
]

function channelLabel(channel?: string | null) {
    const labels: Record<string, string> = { email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp', push: 'Push' }
    return labels[channel ?? ''] ?? (channel ?? '–')
}

function channelColor(channel?: string | null) {
    const colors: Record<string, string> = { email: 'blue', sms: 'violet', whatsapp: 'green', push: 'orange' }
    return colors[channel ?? ''] ?? 'gray'
}

function statusColor(status: string) {
    const colors: Record<string, string> = {
        draft: 'gray', scheduled: 'amber', sending: 'blue', sent: 'green', failed: 'red', canceled: 'orange',
    }
    return colors[status] ?? 'gray'
}

function msgStatusColor(status: string) {
    const colors: Record<string, string> = {
        queued: 'gray', sending: 'blue', sent: 'green', delivered: 'teal', failed: 'red', suppressed: 'orange', skipped: 'amber',
    }
    return colors[status] ?? 'gray'
}

function formatDate(iso: string) {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}
</script>
